import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";

// After a new deploy, the service worker may replace its pre-cache while
// the page still holds old chunk references. A reload picks up the new
// HTML + new SW cache. A sessionStorage guard prevents infinite loops.
// See https://vite.dev/guide/build.html#load-error-handling
window.addEventListener("vite:preloadError", () => {
  const key = "chunk-reload";
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, "1");
    window.location.reload();
  }
});

// Supabase auth emails deliver their tokens in the fragment, and the exact path
// they arrive on depends on the email template -- which lives in a dashboard,
// not in this repo, and has been wrong in three different ways. Rather than
// depending on it, catch the tokens wherever they land: any url carrying an
// access_token in its fragment is an invitation or a password reset, and both
// finish on the set-password page.
//
// Covers /auth-callback.html#..., /auth-callback.html/auth-callback.html#...,
// and a bare domain with the fragment attached. Runs first, because without it
// the app boots, finds no session, and redirects to /login -- discarding the
// tokens with no error at all.
(() => {
  const raw = window.location.hash.replace(/^#/, "");
  if (!raw.includes("access_token=")) return;

  // The fragment may be "access_token=..." or "/some/path#access_token=...",
  // so read from the last "#" or "?" that precedes the token.
  const start = raw.indexOf("access_token=");
  const params = new URLSearchParams(raw.slice(start));
  const access_token = params.get("access_token");
  const refresh_token = params.get("refresh_token");
  if (!access_token || !refresh_token) return;

  const next = new URLSearchParams({
    access_token,
    refresh_token,
    type: params.get("type") ?? "invite",
  });
  window.location.replace(`/#/set-password?${next}`);
})();

// Supabase's OAuth redirect lands on /oauth/consent?authorization_id=... with
// no fragment. This app is a hash router, so an empty hash means "/" and the
// consent request was silently answered with the dashboard -- no error, just
// the wrong page. Move any real path into the fragment before React mounts, so
// the router sees the route that was actually requested.
//
// Runs before createRoot: after mounting, the router has already resolved "/"
// and a rewrite would be a second navigation the user can see.
if (!window.location.hash && window.location.pathname !== "/") {
  const requested = window.location.pathname + window.location.search;
  window.history.replaceState(null, "", `/#${requested}`);
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
